import * as React from 'react';
import { createCustomEqual } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import { useMap } from '@components/map/map-context';

const deepCompareEqualsForMaps = createCustomEqual(
  // @ts-expect-error
  (deepEqual) => (a: any, b: any) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b);
  }
);

/** Context to pass down the map instance to the OverlayViewContainer component */
const MapContext = React.createContext<{
  map: google.maps.Map | undefined;
}>(undefined!);

interface MapProps extends google.maps.MapOptions {
  children?: React.ReactNode;
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (map: google.maps.Map) => void;
  onIdle?: (map: google.maps.Map) => void;
  onResize?: (map: google.maps.Map) => void;
  onZoomChanged?: (map: google.maps.Map) => void;
  style?: React.CSSProperties;
}

export const Map: React.FC<MapProps> = ({
  children,
  onClick,
  onDragEnd,
  onIdle,
  onResize,
  onZoomChanged,
  style,
  ...props
}) => {
  const { map, setMap } = useMap();
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(
        new window.google.maps.Map(ref.current, {
          styles: [
            { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
            {
              featureType: 'poi.park',
              elementType: 'geometry.fill',
              stylers: [{ color: '#BEE1D2' }],
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.text',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'road',
              elementType: 'geometry',
              stylers: [{ color: '#fdfcf8' }],
            },
            {
              featureType: 'road.local',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#B0B0B0' }],
            },
            {
              featureType: 'road.arterial',
              elementType: 'geometry',
              stylers: [{ color: '#fdfcf8' }],
            },
            {
              featureType: 'road.arterial',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#B0B0B0' }],
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry',
              stylers: [{ color: '#fdfcf8' }],
            },
            {
              featureType: 'road.highway',
              elementType: 'geometry.stroke',
              stylers: [{ color: '#e9bc62' }],
            },
            {
              featureType: 'water',
              elementType: 'geometry.fill',
              stylers: [{ color: '#A3D1EC' }],
            },
            {
              featureType: 'water',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#2F4F4F' }],
            },
            {
              featureType: 'locality',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#2F4F4F' }],
            },
            {
              featureType: 'administrative.neighborhood',
              elementType: 'labels.text.fill',
              stylers: [{ color: '#9e9e9e' }],
            },
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'poi.school',
              stylers: [{ visibility: 'off' }],
            },
            {
              featureType: 'transit',
              elementType: 'labels.icon',
              stylers: [{ visibility: 'off' }],
            },
          ],
        })
      );
    }
  }, [ref, map]);

  // Because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(props);
    }
  }, [map, props]);

  React.useEffect(() => {
    if (map) {
      ['click', 'idle', 'zoom_changed', 'dragend', 'resize'].forEach(
        (eventName) => google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }

      if (onZoomChanged) {
        map.addListener('zoom_changed', () => onZoomChanged(map));
      }

      if (onResize) {
        map.addListener('resize', () => onResize(map));
      }

      if (onDragEnd) {
        map.addListener('dragend', () => onDragEnd(map));
      }
    }
  }, [map, onClick, onIdle, onZoomChanged, onResize, onDragEnd]);

  return (
    <MapContext.Provider value={{ map }}>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, {
            ...(child.props as object),
            map,
          } as { map: typeof map });
        } else {
          return null;
        }
      })}
    </MapContext.Provider>
  );
};

function useDeepCompareMemoize(value: any) {
  const ref = React.useRef<any | null>(null);

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: any[]
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}

interface MapMarkerProps extends google.maps.MarkerOptions {
  onDrag?: (e: google.maps.Marker) => void;
  onDragEnd?: (e: google.maps.Marker) => void;
  onDragStart?: (e: google.maps.Marker) => void;
  onClick?: (e: google.maps.Marker) => void;
  priceLabel?: string;
  hover?: boolean;
  index?: number;
}

// Function to calculate the icon based on the label text
export const getMapMarkerIcon = (labelText: string, hover: boolean) => {
  const textLength = labelText.length;
  // Uncomment for BOX
  // const width = textLength * 7 + 16; // Adjusted width to fit the label with padding
  const width = textLength * 5; // Adjusted width to fit the label with padding
  const height = 28; // Fixed height for the pill shape
  const hoverScale = 1.1; // Scale when hovered (20% larger)

  const scale = hover ? hoverScale : 1;

  if (textLength === 0) {
    const fillColor = hover ? '#1E424C' : '#FFFFFF';
    return {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: fillColor,
      fillOpacity: 1,
      strokeColor: '#C0C0C0',
      strokeWeight: 1,
      scale: 12 * scale,
    };
  }

  const fillColor = hover ? '#1E424C' : '#FFFFFF';
  // Uncomment for BOX
  // const radius = 5;
  return {
    path: `M 0,0 h ${width} a ${height / 2},${height / 2} 0 1,1 0,${height} h -${width} a ${height / 2},${height / 2} 0 1,1 0,-${height} z`,
    // Uncomment for BOX
    // path: `M ${radius},0 h ${width - 2 * radius} a ${radius},${radius} 0 0,1 ${radius},${radius} v ${height - 2 * radius} a ${radius},${radius} 0 0,1 -${radius},${radius} h -${width - 2 * radius} a ${radius},${radius} 0 0,1 -${radius},-${radius} v -${height - 2 * radius} a ${radius},${radius} 0 0,1 ${radius},-${radius} z`,
    fillColor: fillColor,
    fillOpacity: 1,
    strokeColor: '#C0C0C0',
    strokeWeight: 1,
    scale: scale, // Use the scale parameter to adjust size
    anchor: new google.maps.Point(width / 2, height / 2),
    labelOrigin: new google.maps.Point(width / 2, height / 2 + 1),
  };
};

// Function to calculate the icon based on the label text
export const getMapMarkerLabel = (labelText: string, hover: boolean) => {
  const textLength = labelText.length;

  if (textLength === 0) {
    return undefined;
  }

  return {
    text: labelText,
    color: hover ? '#FFFFFF' : '#000000',
    fontSize: hover ? '16px' : '14px',
    fontWeight: 'bold',
  };
};

export const MapMarker: React.FC<MapMarkerProps> = ({
  onDrag,
  onDragEnd,
  onDragStart,
  onClick,
  priceLabel,
  hover,
  index,
  ...props
}) => {
  const [marker, setMarker] = React.useState<google.maps.Marker>();

  const zIndex = index ? index : 1;

  React.useEffect(() => {
    if (!marker) {
      const newMarker = new google.maps.Marker({});

      setMarker(newMarker);
    }

    return () => {
      if (marker) {
        marker.setMap(null); // Remove marker from map on unmount
      }
    };
  }, [marker]);

  React.useEffect(() => {
    if (marker) {
      marker.setOptions({
        ...props,
        icon: getMapMarkerIcon(priceLabel || '', false),
        label: getMapMarkerLabel(priceLabel || '', false),
        zIndex: zIndex,
      });

      marker.addListener('mouseover', () => {
        marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1); // Bring marker to the front
        marker.setIcon(getMapMarkerIcon(priceLabel || '', true)); // Increase size on hover
        marker.setLabel(getMapMarkerLabel(priceLabel || '', true)); // Increase size on hover
      });

      marker.addListener('mouseout', () => {
        marker.setZIndex(zIndex); // Reset to the original zIndex
        marker.setIcon(getMapMarkerIcon(priceLabel || '', false)); // Reset size on hover out
        marker.setLabel(getMapMarkerLabel(priceLabel || '', false)); // Increase size on hover
      });

      if (onDrag) marker.addListener('drag', () => onDrag(marker));
      if (onDragEnd) marker.addListener('dragend', () => onDragEnd(marker));
      if (onDragStart)
        marker.addListener('dragstart', () => onDragStart(marker));
      if (onClick) marker.addListener('click', () => onClick(marker));

      if (hover) marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1); // Bring marker to the front
      if (hover) marker.setIcon(getMapMarkerIcon(priceLabel || '', true)); // Increase size on hover
      if (hover) marker.setLabel(getMapMarkerLabel(priceLabel || '', true)); // Increase size on hover
    }
  }, [marker, props, onDrag, onDragEnd, onDragStart, onClick, hover]);

  return null;
};

interface MapCircleProps extends google.maps.CircleOptions {
  onClick?: (e: google.maps.Circle) => void;
}

export const MapCircle: React.FC<MapCircleProps> = ({ onClick, ...props }) => {
  const [circle, setCircle] = React.useState<google.maps.Circle>();

  React.useEffect(() => {
    if (!circle) {
      setCircle(new google.maps.Circle());
    }

    // remove marker from map on unmount
    return () => {
      if (circle) {
        circle.setMap(null);
      }
    };
  }, [circle]);

  React.useEffect(() => {
    if (circle) {
      circle.setOptions(props);

      if (onClick) {
        circle.addListener('click', () => onClick(circle));
      }
    }
  }, [circle, props, onClick]);

  return null;
};

export const MapMarkerDefaultIcon = {
  path: 0.0,
  scale: 11,
  fillColor: '#1E424C',
  fillOpacity: 1,
  strokeColor: '#FFFFFF',
  strokeWeight: 2,
};
